import { apiUrls, buildApiUrl } from '../config/api-config';

export interface ValidatedResponse {
  id: string;
  sessionId: string;
  teamId: string;
  teamName: string;
  challenge: {
    trigger?: string;
    category?: string;
  };
  response: string;
  isValid: boolean;
  timestamp: string;
  createdAt: string;
}

export class ResponseStorageService {
  // Save a validated response
  static async saveResponse(
    sessionId: string,
    teamId: string,
    teamName: string,
    challenge: any,
    response: string,
    isValid: boolean
  ): Promise<{ success: boolean; message: string; responseId?: string }> {
    try {
      const responseData = await fetch(buildApiUrl(`/games/storm/session/${sessionId}/response`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamId,
          teamName,
          challenge,
          response,
          isValid,
          timestamp: new Date().toISOString()
        })
      });

      const result = await responseData.json();
      return result;
    } catch (error) {
      console.error('Error saving response:', error);
      return {
        success: false,
        message: 'Failed to save response'
      };
    }
  }

  // Get all responses for a session
  static async getResponses(sessionId: string): Promise<ValidatedResponse[]> {
    try {
      const response = await fetch(buildApiUrl(`/games/storm/session/${sessionId}/responses`));
      const result = await response.json();
      
      if (result.success) {
        return result.responses || [];
      }
      
      return [];
    } catch (error) {
      console.error('Error loading responses:', error);
      return [];
    }
  }

  // Export responses as text file
  static async exportAsText(sessionId: string): Promise<void> {
    try {
      const response = await fetch(buildApiUrl(`/games/storm/session/${sessionId}/responses?format=text`));
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `storm-responses-${sessionId}.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting text:', error);
    }
  }

  // Export responses as HTML file
  static async exportAsHtml(sessionId: string): Promise<void> {
    try {
      const response = await fetch(buildApiUrl(`/games/storm/session/${sessionId}/responses?format=html`));
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `storm-responses-${sessionId}.html`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting HTML:', error);
    }
  }

  // Get response statistics
  static async getResponseStats(sessionId: string): Promise<{
    total: number;
    valid: number;
    invalid: number;
    validPercentage: number;
  }> {
    try {
      const responses = await this.getResponses(sessionId);
      const valid = responses.filter(r => r.isValid).length;
      const invalid = responses.filter(r => !r.isValid).length;
      const total = responses.length;
      
      return {
        total,
        valid,
        invalid,
        validPercentage: total > 0 ? Math.round((valid / total) * 100) : 0
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return { total: 0, valid: 0, invalid: 0, validPercentage: 0 };
    }
  }
}
